import { useState } from 'react';
import { Search, X } from 'lucide-react';
import type { SearchResult, NavigationCallbacks } from '../types';
import styles from '../ReaderPanels.module.css';
import { compactInputStyle, primaryButtonStyle } from './controls';
import { useTranslation } from '../../../i18n/useTranslation';

interface SearchPanelProps {
  onSearch: (query: string) => Promise<SearchResult[]>;
  onNavigateToResult: NavigationCallbacks['onNavigateToSearchResult'];
}

/**
 * SearchPanel
 *
 * Allows users to search for text within the book.
 * Uses CSS modules for modern, responsive styling.
 */
export default function SearchPanel({ onSearch, onNavigateToResult }: SearchPanelProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Track if search was executed

  const handleSearch = async () => {
    if (!query.trim()) return;

    setIsSearching(true);
    setHasSearched(true); // Mark that a search has been executed
    try {
      const searchResults = await onSearch(query);
      setResults(searchResults);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false); // Reset search state
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    // Reset search state when user modifies the query after a search
    if (hasSearched) {
      setHasSearched(false);
    }
  };

  return (
    <div className={styles.panelContainer} style={{ width: '100%', padding: '20px' }}>
      {/* Search Input Header */}
      <div className={styles.panelHeader} style={{ padding: 0, paddingBottom: 'var(--spacing-3)', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <label htmlFor="search-query-input" className="sr-only">{t('App.Reader.Search.QueryLabel')}</label>
            <input
              id="search-query-input"
              type="text"
              value={query}
              onChange={handleQueryChange}
              onKeyDown={handleKeyDown}
              placeholder={t('App.Reader.Search.Placeholder')}
              style={{ ...compactInputStyle, paddingLeft: '32px', paddingRight: query ? '32px' : '10px' }}
              disabled={isSearching}
              aria-label={t('App.Reader.Search.QueryLabel')}
              autoFocus
            />
            <Search
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '14px',
                height: '14px',
                color: 'var(--color-text-tertiary)',
                pointerEvents: 'none',
              }}
            />
            {query && (
              <button
                onClick={clearSearch}
                aria-label={t('App.Reader.Search.Clear')}
                style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '22px',
                  height: '22px',
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  color: 'var(--color-text-tertiary)',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X style={{ width: '14px', height: '14px' }} />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            disabled={!query.trim() || isSearching}
            style={{
              ...primaryButtonStyle,
              opacity: !query.trim() || isSearching ? 0.5 : 1,
              cursor: !query.trim() || isSearching ? 'not-allowed' : 'pointer',
            }}
            aria-label={t('App.Reader.Search.ButtonLabel')}
            title={t('App.Reader.Search.TooltipOrEnter')}
          >
            {isSearching ? t('App.Reader.Search.Searching') : t('App.Reader.Search.ButtonLabel')}
          </button>
        </div>
      </div>

      {/* Search Results Body */}
      <div className={styles.panelBody}>
        {/* Empty state - no query yet */}
        {!query && !isSearching && !hasSearched && (
          <div className={styles.emptyState}>
            <Search className={styles.emptyStateIcon} />
            <h3 className={styles.emptyStateTitle}>{t('App.Reader.Search.EmptyTitle')}</h3>
            <p className={styles.emptyStateDescription}>
              {t('App.Reader.Search.EmptyDescription')}
            </p>
          </div>
        )}

        {/* Typing state - query entered but search not executed yet */}
        {query && !isSearching && !hasSearched && (
          <div className={styles.emptyState}>
            <Search className={styles.emptyStateIcon} />
            <h3 className={styles.emptyStateTitle}>{t('App.Reader.Search.ReadyTitle')}</h3>
            <p className={styles.emptyStateDescription}>
              {t('App.Reader.Search.ReadyDescription')}
            </p>
          </div>
        )}

        {/* Empty state - no results found (only after search executed) */}
        {results.length === 0 && !isSearching && hasSearched && (
          <div className={styles.emptyState}>
            <Search className={styles.emptyStateIcon} />
            <h3 className={styles.emptyStateTitle}>{t('App.Reader.Search.NoResultsTitle')}</h3>
            <p className={styles.emptyStateDescription}>
              {t('App.Reader.Search.NoResultsDescription', { query })}
            </p>
          </div>
        )}

        {/* Loading state */}
        {isSearching && (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateDescription} aria-label={t('App.Reader.Search.LoadingLabel')}>{t('App.Reader.Search.Searching')}</p>
          </div>
        )}

        {/* Search Results */}
        {results.length > 0 && (
          <ul className={styles.searchResults} role="list">
            {results.map((result) => (
              <li key={result.id}>
                <button
                  type="button"
                  className={styles.searchResult + " w-full text-left"}
                  onClick={() => onNavigateToResult(result)}
                >
                  <div className={styles.searchResultLocation}>
                    {result.locationLabel}
                  </div>
                  <div className={styles.searchResultText}>
                    {result.beforeContext && (
                      <span>{result.beforeContext}...</span>
                    )}
                    <mark>{result.excerpt}</mark>
                    {result.afterContext && (
                      <span>...{result.afterContext}</span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
